import { GlobalFilterConfig } from "../../../dashboard-framework/types";
import { NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import { ATTENDANCE_RATE_STATUSES, FINISHED_STATUSES } from "../shared/sharedMetrics";

// Réplica do Zoho "Info for KPIs service related" — critérios exatos extraídos e validados em spec.md.
// Gestoras de caso = sessões de NFB das clientes que gerem (treatment.client_owner);
// Psicólogas = sessões de Psicologia que deram (sessions.technician).

export const TEAM_KPIS_KEY = "clinic-service-team-kpis";

export const ATTENDANCE_TARGET_PCT = 85;

// Exclusões do Zoho por nome (users.full_name): conta de impersonação e dev.
export const EXCLUDED_PERSON_NAMES = ["impersonate role", "Ricardo Linhares"];

export const teamKpisGlobalFilters: Array<GlobalFilterConfig> = [
    { key: "timeline", type: "datePreset", label: "Período", field: "start_date", defaultPreset: "este_ano" },
    // Âmbito de utilizadores: por defeito só ativos; "Ativos e suspensos" inclui quem já saiu (e reconcilia com o Zoho).
    {
        key: "staffScope",
        type: "choice",
        label: "Utilizadores",
        defaultValue: "active",
        options: [
            { value: "active", label: "Apenas ativos" },
            { value: "all", label: "Ativos e suspensos" },
        ],
    },
];

export const ACTIVE_USER_STATUS = "active";

const ATTENDANCE_STATUS_FILTER = { status: { _in: ATTENDANCE_RATE_STATUSES } };

// Zoho: pivot psicólogas exclui anos ≤2023; gestoras ≤2022 — o limite efetivo vem do filtro global
// (Este ano); estes mínimos só protegem o preset "ultimos_12_meses" de recuar além do Zoho.
const PSYCHOLOGISTS_MIN_START_DATE = { start_date: { _gte: "2024-01-01" } };
const CASE_MANAGERS_MIN_START_DATE = { start_date: { _gte: "2023-01-01" } };

// Critérios ÚNICOS da comparência das psicólogas.
const PSYCHOLOGIST_ATTENDANCE_CRITERIA = [ATTENDANCE_STATUS_FILTER, { service: { _eq: PSY_SERVICE } }, PSYCHOLOGISTS_MIN_START_DATE];

// Fetch raw (AMBAS as vistas — diretora e psicóloga): alimenta os itens do widget E as linhas do
// drill-down — a regra do compromisso (excluir clientes que nunca compareceram) não é exprimível
// no aggregate do Directus, por isso não há caminho por aggregate nesta secção.
export const PSYCHOLOGIST_SESSIONS_FIELDS = [
    "id",
    "status",
    "start_date",
    "technician.id",
    "technician.user_id.first_name",
    "technician.user_id.last_name",
    "technician.user_id.status",
    "treatment.id",
    "treatment.client.client_id",
    "treatment.treatment_status_new",
    "treatment.date_of_start",
].join(",");

export const psychologistSessionsBaseFilter = { _and: PSYCHOLOGIST_ATTENDANCE_CRITERIA };

// Critérios ÚNICOS da comparência das gestoras.
const CASE_MANAGER_ATTENDANCE_CRITERIA = [
    ATTENDANCE_STATUS_FILTER,
    { service: { _eq: NFB_SERVICE } },
    { treatment: { _nnull: true } },
    { treatment: { client_owner: { _nnull: true } } },
    CASE_MANAGERS_MIN_START_DATE,
];

// Fetch raw paginado + rollup client-side (AMBAS as vistas; na vista própria acresce o viewer filter).
// client_owner.id + technicians_id da junção de terapeutas: excluem client-side os tratamentos
// em que a owner também é psicóloga do tratamento (comparação campo-a-campo, impossível no Directus).
// treatment.id + nº do cliente (client_id, ex.: "3701C")/estado/início: só para as linhas do drill-down.
export const CASE_MANAGER_SESSIONS_FIELDS = [
    "id",
    "status",
    "start_date",
    "treatment.id",
    "treatment.client_owner.id",
    "treatment.client_owner.user_id.id",
    "treatment.client_owner.user_id.first_name",
    "treatment.client_owner.user_id.last_name",
    "treatment.client_owner.user_id.status",
    "treatment.theurapists_involved_in_treamtent.technicians_id",
    "treatment.client.client_id",
    "treatment.treatment_status_new",
    "treatment.date_of_start",
].join(",");

export const caseManagerSessionsBaseFilter = { _and: CASE_MANAGER_ATTENDANCE_CRITERIA };

export const psychologistViewerFilter = (viewerUserId: string) => {
    const filter = { technician: { user_id: { id: { _eq: viewerUserId } } } };
    return filter;
};

export const caseManagerViewerFilter = (viewerUserId: string) => {
    const filter = { treatment: { client_owner: { user_id: { id: { _eq: viewerUserId } } } } };
    return filter;
};

// Satisfação: customer_surveys filled no período (date_sent), com os DOIS caminhos de atribuição
// nos fields — agregação e dedup são client-side (groupBy aninhado dá 500; volume é pequeno).
export const SATISFACTION_SURVEYS_FIELDS = [
    "id",
    "would_recommend_clinic",
    "date_sent",
    "moment",
    "other_comments",
    "treatment.client_owner.user_id.id",
    "treatment.client_owner.user_id.first_name",
    "treatment.client_owner.user_id.last_name",
    "treatment.client_owner.user_id.status",
    "treatment.theurapists_involved_in_treamtent.technicians_id.user_id.id",
    "treatment.theurapists_involved_in_treamtent.technicians_id.user_id.first_name",
    "treatment.theurapists_involved_in_treamtent.technicians_id.user_id.last_name",
    "treatment.theurapists_involved_in_treamtent.technicians_id.user_id.status",
].join(",");

export const satisfactionBaseFilter = (dateRange: { gte: string; lt: string }) => {
    const filter = {
        _and: [{ status: { _eq: "filled" } }, { date_sent: { _gte: dateRange.gte, _lt: dateRange.lt } }],
    };
    return filter;
};

export const satisfactionPsychologistViewerFilter = (viewerUserId: string) => {
    const filter = { treatment: { theurapists_involved_in_treamtent: { technicians_id: { user_id: { id: { _eq: viewerUserId } } } } } };
    return filter;
};

export const satisfactionOwnerViewerFilter = (viewerUserId: string) => {
    const filter = { treatment: { client_owner: { user_id: { id: { _eq: viewerUserId } } } } };
    return filter;
};

export const TEAM_KPIS_SURVEY_WARNING =
    "Ver estes dados com cuidado: apenas cerca de 40% das pessoas preenchem os questionários de satisfação. " +
    "Normalmente só responde quem está muito satisfeito ou muito insatisfeito — com 40% de preenchimento, " +
    "as médias são motivadoras mas devem ser lidas com prudência.";

// Tratamentos terminados por gestora de caso: fetch raw paginado (AMBAS as vistas) + rollup client-side —
// a regra "≥1 sessão NFB realizada", a exclusão owner-é-psicóloga e os filtros de grupo/tipo são
// comparações campo-a-campo, impossíveis em aggregates do Directus. diagnose_main.name só para o drill.
export const FINISHED_TREATMENTS_FIELDS = [
    "id",
    "treatment_status_new",
    "age_when_started",
    "date_of_start",
    "date_if_finish",
    "diagnose_main.id",
    "diagnose_main.name",
    "client.client_id",
    "client_owner.id",
    "client_owner.user_id.id",
    "client_owner.user_id.first_name",
    "client_owner.user_id.last_name",
    "client_owner.user_id.status",
    "theurapists_involved_in_treamtent.technicians_id",
    "sessions.service",
    "sessions.status",
].join(",");

export const finishedTreatmentsBaseFilter = {
    _and: [{ treatment_status_new: { _in: FINISHED_STATUSES } }, { client_owner: { _nnull: true } }],
};

// Tratamentos terminados por psicóloga: crédito pelas terapeutas do tratamento (junção expandida
// até ao utilizador); a regra "≥1 sessão de Psicologia realizada" é client-side.
export const PSY_FINISHED_TREATMENTS_FIELDS = [
    "id",
    "treatment_status_new",
    "age_when_started",
    "date_of_start",
    "date_if_finish",
    "diagnose_main.id",
    "diagnose_main.name",
    "client.client_id",
    "theurapists_involved_in_treamtent.technicians_id.id",
    "theurapists_involved_in_treamtent.technicians_id.user_id.id",
    "theurapists_involved_in_treamtent.technicians_id.user_id.first_name",
    "theurapists_involved_in_treamtent.technicians_id.user_id.last_name",
    "theurapists_involved_in_treamtent.technicians_id.user_id.status",
    "sessions.service",
    "sessions.status",
].join(",");

export const psyFinishedTreatmentsBaseFilter = { treatment_status_new: { _in: FINISHED_STATUSES } };

// Vista própria da psicóloga: filtro de UM nível pela junção (id de técnica) — nunca user_id profundo.
export const psyTherapistTreatmentsFilter = (technicianId: number | string) => {
    const filter = { theurapists_involved_in_treamtent: { technicians_id: { _eq: technicianId } } };
    return filter;
};

// Cartões "Clientes ativos" + coluna "Ativos" das duas tabelas: snapshot sem filtro de data; sessões
// trazem start_date para contar qEEGs (Fase 2 do NFB = ≥2 qEEGs realizados em dias distintos).
// Owner e junção de terapeutas (expandida até ao utilizador) alimentam a atribuição nas tabelas.
export const ACTIVE_TREATMENTS_FIELDS = [
    "id",
    "treatment_status_new",
    "diagnose_main.id",
    "diagnose_main.name",
    "client.client_id",
    "age_when_started",
    "date_of_start",
    "date_if_finish",
    "client_owner.id",
    "client_owner.user_id.id",
    "client_owner.user_id.first_name",
    "client_owner.user_id.last_name",
    "client_owner.user_id.status",
    "theurapists_involved_in_treamtent.technicians_id.id",
    "theurapists_involved_in_treamtent.technicians_id.user_id.id",
    "theurapists_involved_in_treamtent.technicians_id.user_id.first_name",
    "theurapists_involved_in_treamtent.technicians_id.user_id.last_name",
    "theurapists_involved_in_treamtent.technicians_id.user_id.status",
    "sessions.service",
    "sessions.status",
    "sessions.start_date",
].join(",");

export const activeTreatmentsBaseFilter = { treatment_status_new: { _eq: "all_good" } };

// services_provided por tratamento (fase da Psicologia), lido via treatment_prescription em chunks
// pelo escalar `treatment` (filtro de raiz, fiável em _in) — portado do Treatment Performance Dashboard.
export const PSY_SPANS_FIELDS = [
    "treatment",
    "services_provided.service",
    "services_provided.session_periodicity",
    "services_provided.date_begin",
    "services_provided.date_end",
].join(",");

export const PSY_SPANS_TREATMENT_CHUNK_SIZE = 100;

export const treatmentsWindowFilter = (dateRange: { gte: string; lt: string }) => {
    const filter = { date_if_finish: { _gte: dateRange.gte, _lt: dateRange.lt } };
    return filter;
};

// Vista individual: filtra pela própria gestora por client_owner de UM nível (id da técnica), nunca por
// client_owner.user_id (filtro profundo que pode ser silenciosamente ignorado e colapsar a clínica toda).
export const treatmentsOwnerFilter = (technicianId: number | string) => {
    const filter = { client_owner: { _eq: technicianId } };
    return filter;
};
